import { Dropdown } from "primereact/dropdown";
import { useEffect, useRef, useState } from "react";
import { Chart } from "primereact/chart";
import {
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  format,
} from "date-fns";
import { generate } from "patternomaly";
import { ProgressSpinner } from "primereact/progressspinner";
import { AnimatePresence, motion } from "framer-motion";
import { Skeleton } from "primereact/skeleton";

const OwnerDashboardGeneralGraph = (props) => {
  const { data, loading, filterValue } = props;
  const [granularity, setGranularity] = useState("revenue_daily");

  const [revenueChartData, setRevenueChartData] = useState({});
  const [revenueChartOptions, setRevenueChartOptions] = useState({});

  const [transactionChartData, setTransactionChartData] = useState({});
  const [transactionChartOptions, setTransactionChartOptions] = useState({});

  const generateChartBasedOnGranularity = (granularity) => {
    if (data && granularity) {
      if (granularity === "revenue_daily") {
        generateChartData(data.revenue_daily);
      }
      if (granularity === "revenue_weekly") {
        generateChartData(data.revenue_weekly);
      }
      if (granularity === "revenue_monthly") {
        generateChartData(data.revenue_monthly);
      }
      if (granularity === "revenue_yearly") {
        generateChartData(data.revenue_yearly);
      }
    }
  };

  useEffect(() => {
    generateChartBasedOnGranularity(granularity);
  }, [granularity, data]);

  const getDateDifference = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = endDate.getTime() - startDate.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
  };

  const getAllowedGranularities = (days) => {
    if (days <= 7) return [{ label: "Daily", value: "revenue_daily" }];
    if (days <= 31)
      return [
        { label: "Daily", value: "revenue_daily" },
        { label: "Weekly", value: "revenue_weekly" },
      ];
    if (days <= 365)
      return [
        { label: "Weekly", value: "revenue_weekly" },
        { label: "Monthly", value: "revenue_monthly" },
      ];
    if (days <= 1830)
      return [
        { label: "Monthly", value: "revenue_monthly" },
        { label: "Yearly", value: "revenue_yearly" },
      ];
    return [{ label: "Yearly", value: "revenue_yearly" }];
  };

  useEffect(() => {
    const dateDiff = getDateDifference(
      filterValue.dari_tanggal,
      filterValue.sampai_tanggal
    );
    const options = getAllowedGranularities(dateDiff);

    setGranularities(options);

    // If current granularity is no longer valid, auto-adjust it
    if (!options.some((g) => g.value === granularity)) {
      setGranularity(options[0].value); // dropdown & chart will update via 2nd useEffect
    }

    // Don't regenerate chart here — wait for granularity to change
  }, [filterValue.dari_tanggal, filterValue.sampai_tanggal]);

  const [granularities, setGranularities] = useState([
    { label: "Daily", value: "revenue_daily" },
    { label: "Weekly", value: "revenue_weekly" },
    { label: "Monthly", value: "revenue_monthly" },
    { label: "Yearly", value: "revenue_yearly" },
  ]);

  const getPeriodKey = (item, granularity) => {
    switch (granularity) {
      case "revenue_daily":
        return item.period_date;
      case "revenue_weekly":
        return item.period_week;
      case "revenue_monthly":
        return item.period_month;
      case "revenue_yearly":
        return item.period_year;
      default:
        return null;
    }
  };

  const generateRevenueChartData = (data, labels, intervals, prevIntervals) => {
    const currentRevenue = data.current;
    const prevRevenue = data.previous || [];

    const revenueMap = currentRevenue.reduce((acc, item) => {
      const key = getPeriodKey(item, granularity);
      if (key !== null) acc[key] = item.revenue;
      return acc;
    }, {});

    const prevRevenueMap = prevRevenue.reduce((acc, item) => {
      const key = getPeriodKey(item, granularity);
      if (key !== null) acc[key] = item.revenue;
      return acc;
    }, {});

    const trendMap = currentRevenue.reduce((acc, item) => {
      const key = getPeriodKey(item, granularity);
      if (key !== null) acc[key] = item.trend_revenue;
      return acc;
    }, {});

    const revenues = intervals.map((d) => {
      return revenueMap[d] || 0;
    });

    const prevRevenues = prevIntervals.map((d) => {
      return prevRevenueMap[d] || 0;
    });

    const trends = intervals.map((d) => {
      return trendMap[d] ?? null;
    });

    const chartData = {
      labels,
      datasets: [
        {
          label: "TrendLine",
          data: trends,
          borderColor: "rgba(219, 24, 24, 0.8)",
          tension: 0,
        },
        ...(granularity !== "revenue_daily"
          ? [
              {
                label: "Previous Period Revenue",
                data: prevRevenues,
                borderColor: "rgba(87, 88, 86, 0.8)",
                tension: 0.3,
              },
            ]
          : []),
        {
          label: "Revenue",
          data: revenues,
          fill: true,
          backgroundColor: (context) => {
            const { ctx, chartArea } = context.chart;
            if (!chartArea) return "rgba(74, 222, 128, 0.3)"; // fallback while loading

            const gradient = ctx.createLinearGradient(
              0,
              0,
              0,
              chartArea.bottom
            );
            gradient.addColorStop(0, "rgba(74, 222, 128, 0.69)"); // green-400 (from)
            gradient.addColorStop(0.5, "rgba(16, 185, 129, 0.4)"); // emerald-500 (to)
            gradient.addColorStop(1, "rgba(255, 255, 255, 0)"); // transparent white
            return gradient;
          },
          borderColor: "rgba(16, 185, 129, 0.8)", // emerald-500 border
          tension: 0.3,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: "#000000",
          },
        },
      },
      scales: {
        x: {
          ticks: { color: "#000000" },
        },
        y: {
          ticks: {
            color: "#000000",
            callback: function (value) {
              if (value >= 1_000_000_000) {
                return (
                  (value / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + " M"
                ); // Milliar
              } else if (value >= 1_000_000) {
                return (
                  (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + " jt"
                ); // Juta
              } else if (value >= 1_000) {
                return (value / 1_000).toFixed(1).replace(/\.0$/, "") + " rb"; // Ribu
              }
              return value;
            },
          },
        },
      },
    };

    setRevenueChartData(chartData);
    setRevenueChartOptions(chartOptions);
  };

  const generateTransactionChartData = (
    data,
    labels,
    intervals,
    prevIntervals
  ) => {
    const currentTransaction = data.current;
    const prevTransaction = data.previous || [];

    const transactionMap = currentTransaction.reduce((acc, item) => {
      const key = getPeriodKey(item, granularity);
      if (key !== null) acc[key] = item.receipts;
      return acc;
    }, {});

    const prevTransactionMap = prevTransaction.reduce((acc, item) => {
      const key = getPeriodKey(item, granularity);
      if (key !== null) acc[key] = item.receipts;
      return acc;
    }, {});

    const trendMap = currentTransaction.reduce((acc, item) => {
      const key = getPeriodKey(item, granularity);
      if (key !== null) acc[key] = item.trend_receipts;
      return acc;
    }, {});

    const transactions = intervals.map((d) => {
      return transactionMap[d] || 0;
    });

    const prevTransactions = prevIntervals.map((d) => {
      return prevTransactionMap[d] || 0;
    });

    const trends = intervals.map((d) => {
      return trendMap[d] || 0;
    });

    const chartData = {
      labels,
      datasets: [
        {
          label: "TrendLine",
          data: trends,
          borderColor: "rgba(219, 24, 24, 0.8)",
          tension: 0,
        },
        ...(granularity !== "revenue_daily"
          ? [
              {
                label: "Previous Period Transaction",
                data: prevTransactions,
                borderColor: "rgba(87, 88, 86, 0.8)",
                tension: 0.3,
              },
            ]
          : []),

        {
          label: "Transaction",
          data: transactions,
          fill: true,
          backgroundColor: (context) => {
            const { ctx, chartArea } = context.chart;
            if (!chartArea) return "rgba(79, 70, 229, 0.3)"; // fallback while loading

            const gradient = ctx.createLinearGradient(
              0,
              0,
              0,
              chartArea.bottom
            );
            gradient.addColorStop(0, "rgba(28, 184, 106, 0.69)");
            gradient.addColorStop(0.5, "rgba(28, 184, 106, 0.4)");
            gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
            return gradient;
          },
          borderColor: "rgba(28, 184, 106, 0.8)",
          tension: 0.3,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: "#000000",
          },
        },
      },
      scales: {
        x: {
          ticks: { color: "#000000" },
        },
        y: {
          ticks: {
            color: "#000000",
            callback: function (value) {
              if (value >= 1_000_000_000) {
                return (
                  (value / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + " M"
                ); // Milliar
              } else if (value >= 1_000_000) {
                return (
                  (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + " jt"
                ); // Juta
              } else if (value >= 1_000) {
                return (value / 1_000).toFixed(1).replace(/\.0$/, "") + " rb"; // Ribu
              }
              return value;
            },
          },
        },
      },
    };

    setTransactionChartData(chartData);
    setTransactionChartOptions(chartOptions);
  };

  function formatPeriodWeek(periodWeek) {
    const [year, week] = periodWeek.split("-");
    return `Week ${parseInt(week, 10)} ${year}`;
  }

  function formatYearMonth(input) {
    const [year, month] = input.split("-");
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // month is "01", so convert to zero-based index for array
    const monthIndex = parseInt(month, 10) - 1;
    return `${monthNames[monthIndex]} ${year}`;
  }

  const generateChartData = (data) => {
    let intervals = [];
    let prevIntervals = [];
    switch (granularity) {
      case "revenue_daily":
        intervals = data?.current?.map((item) => item.period_date);
        prevIntervals = data?.previous?.map((item) => item.period_date);
        break;
      case "revenue_weekly":
        intervals = data?.current?.map((item) => item.period_week);
        prevIntervals = data?.previous?.map((item) => item.period_week);
        break;
      case "revenue_monthly":
        intervals = data?.current?.map((item) => item.period_month);
        prevIntervals = data?.previous?.map((item) => item.period_month);
        break;
      case "revenue_yearly":
        intervals = data?.current?.map((item) => item.period_year);
        prevIntervals = data?.previous?.map((item) => item.period_year);
        break;
      default:
        intervals = [];
        prevIntervals = [];
    }

    const labels = intervals.map((d) => {
      if (granularity === "revenue_daily" || granularity === "revenue_yearly") {
        return d;
      }
      if (granularity === "revenue_weekly") {
        return formatPeriodWeek(d);
      }
      if (granularity === "revenue_monthly") {
        return formatYearMonth(d);
      }
      return "";
    });

    generateRevenueChartData(data, labels, intervals, prevIntervals);
    generateTransactionChartData(data, labels, intervals, prevIntervals);
  };

  return (
    <div className="w-full col-center-start gap-4 h-full">
      {/* Graph Section */}
      <div className="col-center-start elevated-card-tight-vertical p-2 gap-8 !shadow-md w-full border border-gray-300">
        <div className="w-full col-center-start gap-2">
          <div className="w-full row-between-center">
            <label className="text-xl font-semibold text-black">
              Revenue Trend
            </label>
            <Dropdown
              value={granularity}
              options={granularities}
              optionLabel="label"
              onChange={(e) => setGranularity(e.value)}
              className="w-[15rem] text-lg font-medium"
              loading={loading}
            />
          </div>
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="skeleton"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full h-[15rem] sm:h-[25rem]"
              >
                <Skeleton width="100%" height="100%" borderRadius="12px" />
              </motion.div>
            ) : (
              <motion.div
                key="chart"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="w-full h-[15rem] sm:h-[25rem]"
              >
                <Chart
                  className="w-full h-[15rem] sm:h-[25rem]"
                  type="line"
                  data={revenueChartData}
                  options={revenueChartOptions}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="w-full col-center-start gap-2">
          <label className="text-xl font-semibold text-black">
            Transaction Count
          </label>
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="skeleton"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full h-[15rem] sm:h-[25rem]"
              >
                <Skeleton width="100%" height="100%" borderRadius="12px" />
              </motion.div>
            ) : (
              <motion.div
                key="chart"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="w-full h-[15rem] sm:h-[25rem]"
              >
                <Chart
                  className="w-full h-[15rem] sm:h-[25rem]"
                  type="line"
                  data={transactionChartData}
                  options={transactionChartOptions}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default OwnerDashboardGeneralGraph;
